import { Project, ProjectSubmission } from "./project-objects";
import { Instrument } from "./music-objects";
import { Artist } from "./library-objects";
import { Utils } from "./utils";

export const allowedRoles = ["ConductionAdmin", "ClassroomAdmin", "Student", "Guest", "User", "Transcriber", "Instrumentalist"] as const;
export type Role = (typeof allowedRoles)[number];

export const allowedEventRoles = ["EventAdmin", "Participant", "Instrumentalist"] as const;
export type EventRole = (typeof allowedEventRoles)[number];

export class User {
	public readonly id: string;
	public readonly username: string;
	public readonly firstName: string | null;
	public readonly lastName: string | null;
	public readonly email: string | null;
	public readonly dateCreated: Date | undefined;
	public readonly roles: Role[];
	public readonly classroom: Classroom | undefined;
	public readonly projectIds: string[];
	public readonly completedBeginnerTutorial: boolean;
	public readonly artists: Artist[];
	public readonly agreedToCurrentTos: boolean | undefined;
	public readonly uploadedProfileImage: boolean = false;
	public readonly tokens: ProjectSubmissionToken[];

	private constructor(id: string, username: string, firstName: string | null, lastName: string | null, email: string | undefined, dateCreated: Date, roles: Role[], classroom: Classroom | undefined, projectIds: string[], completedBeginnerTutorial: boolean, artists: Artist[], agreedToCurrentTos: boolean | undefined, uploadedProfileImage: boolean, tokens: ProjectSubmissionToken[]) {
		this.id = id;
		this.username = username;
		this.firstName = firstName;
		this.lastName = lastName;
		this.email = email || null;
		this.dateCreated = dateCreated;
		this.roles = roles;
		this.classroom = classroom;
		this.projectIds = projectIds;
		this.completedBeginnerTutorial = completedBeginnerTutorial;
		this.artists = artists;
		this.agreedToCurrentTos = agreedToCurrentTos;
		this.uploadedProfileImage = uploadedProfileImage;
		this.tokens = tokens;
	}

	public getRemainingActiveCredits(): number {
		return this.tokens.filter((currentToken) => currentToken.isActive()).reduce((acc: number, token: ProjectSubmissionToken) => acc + token.getRemainingActiveCredits(), 0);
	}

	public static fromJson(json: any): User {
		return new User(
			json.userId,
			json.userName,
			json.firstName,
			json.lastName,
			json.email,
			new Date(json.dateCreated),
			json.roles?.filter((role: string) => allowedRoles.includes(role as Role)) ?? [],
			json.classroom ? Classroom.fromJson(json.classroom) : undefined,
			json.projectIds,
			json.completedBeginnerTutorial,
			json.artists.map((artist: any) => Artist.fromJson(artist)),
			json.agreedToCurrentTos ?? undefined,
			json.uploadedProfileImage ?? false,
			json.tokens.map((token: any) => ProjectSubmissionToken.fromJson(token))
		);
	}

	public static getGuestUser() {
		return new User(
			"guest",
			"Guest",
			null,
			null,
			undefined,
			new Date(),
			["Guest"],
			undefined,
			[],
			false,
			[],
			undefined,
			false,
			[]
		);
	}
}

export class Classroom {
	private classroomAdmins: User[] | undefined;
	private students: User[] | undefined;
	private projects: Project[] | undefined;

	public readonly id: string;
	public readonly name: string;
	public readonly desiredName: string;
	public readonly dateCreated: Date;
	public readonly classroomAdminLimit: number;
	public readonly desiredClassroomAdminLimit: number;
	public readonly studentLimit: number;
	public readonly desiredStudentLimit: number;
	public readonly projectCount: number;
	public readonly submissionCount: number;
	public readonly tokens: ProjectSubmissionToken[] | undefined;
	public readonly songIds: string[];
	public readonly classroomAdminAccessCode: string | undefined;
	public readonly studentAccessCode: string | undefined;

	private constructor(
		id: string,
		name: string,
		dateCreated: Date,
		classroomAdminLimit: number,
		studentLimit: number,
		projectCount: number,
		submissionCount: number,
		tokens: ProjectSubmissionToken[] | undefined,
		songIds: string[],
		classroomAdminAccessCode?: string,
		studentAccessCode?: string,
		desiredClassroomAdminLimit?: number,
		desiredStudentLimit?: number,
		desiredName?: string
	) {
		this.id = id;
		this.name = name;
		this.desiredName = desiredName ?? name;
		this.dateCreated = dateCreated;
		this.classroomAdminLimit = classroomAdminLimit;
		this.studentLimit = studentLimit;
		this.desiredClassroomAdminLimit = desiredClassroomAdminLimit ?? classroomAdminLimit;
		this.desiredStudentLimit = desiredStudentLimit ?? studentLimit;
		this.projectCount = projectCount;
		this.submissionCount = submissionCount;
		this.tokens = tokens;
		this.songIds = songIds;
		this.classroomAdminAccessCode = classroomAdminAccessCode;
        this.studentAccessCode = studentAccessCode;
	}

	public setDesiredName(desiredName: string): Classroom {
		const newClassroom = new Classroom(
			this.id,
			this.name,
			this.dateCreated,
			this.classroomAdminLimit,
			this.studentLimit,
			this.projectCount,
			this.submissionCount,
			this.tokens,
			this.songIds,
			this.classroomAdminAccessCode,
			this.studentAccessCode,
			this.desiredClassroomAdminLimit,
			this.desiredStudentLimit,
			desiredName
		);
		newClassroom.classroomAdmins = this.classroomAdmins;
		newClassroom.students = this.students
		newClassroom.projects = this.projects;
		return newClassroom;
	}

	public setDesiredClassroomAdminLimit(desiredLimit: number): Classroom {
		const newClassroom = new Classroom(
			this.id,
			this.name,
			this.dateCreated,
			this.classroomAdminLimit,
			this.studentLimit,
			this.projectCount,
			this.submissionCount,
			this.tokens,
			this.songIds,
			this.classroomAdminAccessCode,
			this.studentAccessCode,
			desiredLimit,
			this.desiredStudentLimit,
			this.desiredName
		);
		newClassroom.classroomAdmins = this.classroomAdmins;
		newClassroom.students = this.students
		newClassroom.projects = this.projects;
		return newClassroom;
	}

	public setDesiredStudentLimit(desiredLimit: number): Classroom {
		const newClassroom = new Classroom(
			this.id,
			this.name,
			this.dateCreated,
			this.classroomAdminLimit,
			this.studentLimit,
			this.projectCount,
			this.submissionCount,
			this.tokens,
			this.songIds,
			this.classroomAdminAccessCode,
			this.studentAccessCode,
			this.desiredClassroomAdminLimit,
			desiredLimit,
			this.desiredName
		);
		newClassroom.classroomAdmins = this.classroomAdmins;
		newClassroom.students = this.students
		newClassroom.projects = this.projects;
		return newClassroom;
	}

	public loadProjects(projects: Project[]): void {
		this.projects = projects;
	}

	public getProjects(): Project[] | undefined {
		return this.projects;
	}

	public loadClassroomAdmins(classroomAdmins: User[]): void {
		this.classroomAdmins = classroomAdmins;
	}

	public getClassroomAdmins(): User[] | undefined {
		return this.classroomAdmins;
	}

	public loadClassroomStudents(classroomStudents: User[]): void {
		this.students = classroomStudents;
	}

	public getClassroomStudents(): User[] | undefined {
		return this.students;
	}

	public getRemainingActiveCredits(): number | undefined {
		return this.tokens?.filter((currentToken) => currentToken.isActive()).reduce((acc: number, token: ProjectSubmissionToken) => acc + token.getRemainingActiveCredits(), 0) ?? undefined;
	}

	public static fromJson(json: any): Classroom {
		return new Classroom(
			json.id,
			json.name,
			new Date(json.dateCreated),
			json.classroomAdminLimit,
			json.studentLimit,
			json.projectCount,
			json.submissionCount,
			json.tokens.map((token: any) => ProjectSubmissionToken.fromJson(token)),
			json.defaultSongIds,
			json.classroomAdminAccessCode,
			json.studentAccessCode
		);
	}
}

export class ProjectSubmissionToken {
	public readonly id: string;
	public readonly credits: number;
	public readonly dateCreated: Date;
	public readonly startDate: Date;
	public readonly expirationDate: Date;
	public readonly submissions: ProjectSubmission[];

	private constructor(
		id: string,
		credits: number,
		dateCreated: Date,
		startDate: Date,
		expirationDate: Date,
		submissions: ProjectSubmission[]
	) {
		this.id = id;
		this.credits = credits;
		this.dateCreated = dateCreated;
		this.startDate = startDate;
		this.expirationDate = expirationDate;
		this.submissions = submissions;
	}

	public getRemainingActiveCredits(): number {
		if (!this.isActive()) {
			return 0;
		}
		return this.credits - this.submissions.length;
	}

	public getActiveCredits(): number {
		if (!this.isActive()) {
			return 0;
		}
		return this.credits;
	}

	public passedStartDate() {
		// Get the current UTC date and time
		const now = new Date();
		return now >= this.startDate;
	}
	
	public isExpired() {
		// Get the current UTC date and time
		const now = new Date();
		return now > this.expirationDate;
	}

	public isActive() {
		return this.passedStartDate() && !this.isExpired();
	}

	public static fromJson(json: any): ProjectSubmissionToken {
		return new ProjectSubmissionToken(
			json.id,
			json.credits,
			Utils.assumeUtcDate(json.created),
			Utils.assumeUtcDate(json.start ?? 0),
			Utils.assumeUtcDate(json.expiration),
			json.submissions.map((submission: any) => ProjectSubmission.fromJson(submission))
		);
	}
}

export class LiveEvent {
	private eventAdmins: User[] | undefined;
	private participants: User[] | undefined;
	private instrumentalists: User[] | undefined;

	private projectsReadyForReview: Project[] | undefined;
	private projectPerformanceQueue: Project[] | undefined;
	private projectPerformanceQueuePosition: number | undefined;

	private instruments: Instrument[] | undefined;

	public readonly id: string;
	public readonly name: string;
	public readonly desiredName: string;
	public readonly dateCreated: Date;
	public readonly startTime: Date;
	public readonly desiredStartTime: Date | undefined;
	public readonly endTime: Date;
	public readonly desiredEndTime: Date | undefined;
	public readonly eventAdminLimit: number | null;
	public readonly desiredEventAdminLimit: number | null;
	public readonly participantLimit: number | null;
	public readonly desiredParticipantLimit: number | null;
	public readonly songIds: string[];
	public readonly eventAdminAccessCode: string | undefined;
	public readonly participantAccessCode: string | undefined;
	public readonly instrumentalistAccessCode: string | undefined;

	private constructor(
		id: string,
		name: string,
		dateCreated: Date,
		startTime: Date,
		endTime: Date,
		eventAdminLimit: number | null,
		participantLimit: number | null,
		songIds: string[],
		eventAdminAccessCode?: string,
		participantAccessCode?: string,
		instrumentalistAccessCode?: string,
		desiredEventAdminLimit?: number | null,
		desiredParticipantLimit?: number | null,
		desiredName?: string
	) {
		this.id = id;
		this.name = name;
		this.desiredName = desiredName ?? name;
		this.dateCreated = dateCreated;
		this.startTime = startTime;
		this.endTime = endTime;
		this.eventAdminLimit = eventAdminLimit;
		this.participantLimit = participantLimit;
		this.desiredEventAdminLimit = desiredEventAdminLimit ?? eventAdminLimit;
		this.desiredParticipantLimit = desiredParticipantLimit ?? participantLimit;
		this.songIds = songIds;
		this.eventAdminAccessCode = eventAdminAccessCode;
		this.participantAccessCode = participantAccessCode;
		this.instrumentalistAccessCode = instrumentalistAccessCode;
	}

	public setDesiredName(desiredName: string): LiveEvent {
		const newLiveEvent = new LiveEvent(
			this.id,
			this.name,
			this.dateCreated,
			this.startTime,
			this.endTime,
			this.eventAdminLimit,
			this.participantLimit,
			this.songIds,
			this.eventAdminAccessCode,
			this.participantAccessCode,
			this.instrumentalistAccessCode,
			this.desiredEventAdminLimit,
			this.desiredParticipantLimit,
			desiredName
		);
		newLiveEvent.eventAdmins = this.eventAdmins;
		newLiveEvent.participants = this.participants;
		newLiveEvent.instrumentalists = this.instrumentalists;
		newLiveEvent.projectsReadyForReview = this.projectsReadyForReview;
		newLiveEvent.projectPerformanceQueue = this.projectPerformanceQueue;
		newLiveEvent.projectPerformanceQueuePosition = this.projectPerformanceQueuePosition;
		return newLiveEvent;
	}

	public setDesiredEventAdminLimit(desiredLimit: number | null): LiveEvent {
		const newLiveEvent = new LiveEvent(
			this.id,
			this.name,
			this.dateCreated,
			this.startTime,
			this.endTime,
			this.eventAdminLimit,
			this.participantLimit,
			this.songIds,
			this.eventAdminAccessCode,
			this.participantAccessCode,
			this.instrumentalistAccessCode,
			desiredLimit,
			this.desiredParticipantLimit,
			this.desiredName
		);
		newLiveEvent.eventAdmins = this.eventAdmins;
		newLiveEvent.participants = this.participants;
		newLiveEvent.instrumentalists = this.instrumentalists;
		newLiveEvent.projectsReadyForReview = this.projectsReadyForReview;
		newLiveEvent.projectPerformanceQueue = this.projectPerformanceQueue;
		newLiveEvent.projectPerformanceQueuePosition = this.projectPerformanceQueuePosition;
		return newLiveEvent;
	}

	public setDesiredStudentLimit(desiredLimit: number | null): LiveEvent {
		const newLiveEvent = new LiveEvent(
			this.id,
			this.name,
			this.dateCreated,
			this.startTime,
			this.endTime,
			this.eventAdminLimit,
			this.participantLimit,
			this.songIds,
			this.eventAdminAccessCode,
			this.participantAccessCode,
			this.instrumentalistAccessCode,
			this.desiredEventAdminLimit,
			desiredLimit,
			this.desiredName
		);
		newLiveEvent.eventAdmins = this.eventAdmins;
		newLiveEvent.participants = this.participants;
		newLiveEvent.instrumentalists = this.instrumentalists;
		newLiveEvent.projectsReadyForReview = this.projectsReadyForReview;
		newLiveEvent.projectPerformanceQueue = this.projectPerformanceQueue;
		newLiveEvent.projectPerformanceQueuePosition = this.projectPerformanceQueuePosition;
		return newLiveEvent;
	}

	public loadProjectLists(projectsReadyForReview: Project[], projectPerformanceQueue: Project[], projectPerformanceQueuePosition: number): void {
		this.projectsReadyForReview = projectsReadyForReview;
		this.projectPerformanceQueue = projectPerformanceQueue;
		this.projectPerformanceQueuePosition = projectPerformanceQueuePosition;
	}

	public getProjectsReadyForReview(): Project[] | undefined {
		return this.projectsReadyForReview
	}

	public getProjectPerformanceQueue(): Project[] | undefined {
		return this.projectPerformanceQueue;
	}

	public getProjectPerformanceQueuePosition(): number | undefined {
		return this.projectPerformanceQueuePosition;
	}

	public loadEventAdmins(eventAdmins: User[]): void {
		this.eventAdmins = eventAdmins;
	}

	public getEventAdmins(): User[] | undefined {
		return this.eventAdmins;
	}

	public loadParticipants(participants: User[]): void {
		this.participants = participants;
	}

	public getParticipants(): User[] | undefined {
		return this.participants;
	}

	public loadInstrumentalists(instrumentalists: User[]): void {
		this.instrumentalists = instrumentalists;
	}

	public getInstrumentalists(): User[] | undefined {
		return this.instrumentalists;
	}

	public loadInstruments(instruments: Instrument[]): void {
		this.instruments = instruments;
	}

	public getInstruments(): Instrument[] | undefined {
		return this.instruments;
	}

	public static fromJson(json: any): LiveEvent {
		return new LiveEvent(
			json.id,
			json.name,
			new Date(json.dateCreated),
			new Date(json.startTime),
			new Date(json.endTime),
			json.eventAdminLimit,
			json.participantLimit,
			json.songIds,
			json.eventAdminAccessCode,
			json.participantAccessCode,
			json.instrumentalistAccessCode
		);
	}
}

export class Location {
	public readonly id: string;
	public readonly displayName: string;
	public readonly city: string;
	public readonly region: string | null;
	public readonly countryCode: string;
	public readonly postalCode: string | null;
	public readonly primaryColor: string | null;
	public readonly secondaryColor: string | null;
	public readonly latitude: number;
	public readonly longitude: number;

	private constructor(
		id: string,
		displayName: string,
		city: string,
		region: string | null,
		countryCode: string,
		postalCode: string | null,
		primaryColor: string | null,
		secondaryColor: string | null,
		latitude: number,
		longitude: number
	) {
		this.id = id;
		this.displayName = displayName;
		this.city = city;
		this.region = region;
		this.countryCode = countryCode;
		this.postalCode = postalCode;
		this.primaryColor = primaryColor;
		this.secondaryColor = secondaryColor;
		this.latitude = latitude;
		this.longitude = longitude;
	}

	public static fromJson(json: any): Location {
		return new Location(
			json.locationId,
			json.displayName,
			json.city,
			json.region ?? null,
			json.countryCode,
			json.postalCode ?? null,
			json.primaryColor ?? null,
			json.secondaryColor ?? null,
			json.latitude,
			json.longitude
		);
	}
}